import type { CardsView } from '@wyn/contracts';
import { cardName } from '@wyn/game-core';
import type { TurnGameProps } from './registry.tsx';

type Card = CardsView['current'];
const red = (c: Card) => c.suit === '♥' || c.suit === '♦';

function Face({ card, label, hidden }: { card: Card | null; label: string; hidden?: boolean }) {
  return (
    <div className="space-y-1 text-center">
      <div
        aria-label={card && !hidden ? `${label}: ${cardName(card)}` : `${label}: face down`}
        className={`flex h-[118px] w-[84px] items-center justify-center rounded-md text-3xl font-bold ${
          card && !hidden ? `bg-[#f1e9d6] ${red(card) ? 'text-[#b33a2c]' : 'text-[#1b1a17]'}` : 'bg-[repeating-linear-gradient(45deg,#6b2a22_0_6px,#4a1c16_6px_12px)] text-[#f1e9d6]'
        }`}
      >
        {card && !hidden ? cardName(card) : ''}
      </div>
      <div className="text-xs text-ink-faint">{label}</div>
    </div>
  );
}

/** The Black Dog: guess higher or lower; peek at your marks if you dare. The deck stays on the server. */
export function CardsGame({ view, busy, onMove }: TurnGameProps<CardsView>) {
  const done = view.over !== null;
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-dim">
        Guess whether the next card is higher or lower. Each right guess grows the pot by the odds; equal ranks are a push. Peek at your marks to be sure, but a sure guess pays only ×1.1, and every peek risks being seen: the hotter the room, the likelier. Get caught and you lose everything. Cash out whenever you like; up to {view.maxStreak} cards.
      </p>
      <div className="flex justify-between font-mono">
        <span>Pot <b className="text-gilt" data-testid="cards-pot">{view.pot}</b></span>
        <span>Streak <b>{view.streak}</b>/{view.maxStreak}</span>
        <span className="text-ink-faint">{view.cardsLeft} left</span>
      </div>
      <div className="flex justify-center gap-5">
        <Face card={view.current} label="On the table" />
        <Face card={view.peeked} label={view.peeked ? 'Your mark says…' : 'Next'} hidden={!view.peeked} />
      </div>
      <p className="text-center text-sm" role="status">{view.say}</p>
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-ink-dim"><span>Suspicion</span><span className="font-mono">{Math.round(view.suspicion)}%</span></div>
        <div className="h-2 bg-rule" role="meter" aria-label="Suspicion" aria-valuenow={Math.round(view.suspicion)} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-blood" style={{ width: `${Math.min(100, view.suspicion)}%` }} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="btn py-3" disabled={busy || done || !view.higher} onClick={() => onMove({ type: 'higher' })}>
          ▲ Higher <span className="font-mono text-xs">{view.higher ? `×${view.higher.toFixed(2)}` : '—'}</span>
        </button>
        <button type="button" className="btn py-3" disabled={busy || done || !view.lower} onClick={() => onMove({ type: 'lower' })}>
          ▼ Lower <span className="font-mono text-xs">{view.lower ? `×${view.lower.toFixed(2)}` : '—'}</span>
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="btn-quiet" disabled={busy || done || !!view.peeked} onClick={() => onMove({ type: 'peek' })}>👁 Check your marks</button>
        <button type="button" className="btn-quiet" disabled={busy || done} onClick={() => onMove({ type: 'cash' })}>💰 Cash out · {view.pot}</button>
      </div>
    </div>
  );
}
